import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { X, Loader2, CloudRain } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useModalBack } from "@/lib/modalBack";

// Batch 5: rain-day shift — preview first (what moves, what stays), then
// Apply. One undo entry restores everything; locked tasks never move.
export default function RainDaySheet({ open, onClose, onApplied, initialDate = "" }) {
  const { t } = useTranslation();
  const [date, setDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useModalBack(open, onClose);

  useEffect(() => { if (open) { setDate(initialDate || ""); setPreview(null); } }, [open, initialDate]);

  if (!open) return null;

  const doPreview = async () => {
    if (busy || !date) return;
    setBusy(true);
    try {
      const { data } = await api.post("/schedule/rain-day/preview", { date });
      setPreview(data);
    } catch { toast.error(t("toast_failed")); }
    setBusy(false);
  };

  const apply = async () => {
    if (busy || !date) return;
    setBusy(true);
    try {
      const { data } = await api.post("/schedule/rain-day", { date });
      onApplied?.(data);
      setPreview(null);
      setDate("");
      onClose();
    } catch { toast.error(t("toast_failed")); }
    setBusy(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 sm:items-center" onClick={onClose} data-testid="rain-sheet">
      <div className="w-full max-w-md rounded-t-3xl bg-white p-5 pb-[calc(20px+env(safe-area-inset-bottom,0px))] sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-base font-extrabold text-slate-900">
            <CloudRain className="h-5 w-5 text-brand" />{t("rain_title")}
          </h3>
          <button onClick={onClose} data-testid="rain-close" aria-label={t("action_cancel")}
            className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-full text-slate-400 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-1 text-xs text-slate-500">{t("rain_body")}</p>
        <input type="date" value={date} onChange={(e) => { setDate(e.target.value); setPreview(null); }}
          data-testid="rain-date" className="mt-3 h-12 w-full rounded-xl border border-slate-300 px-3 text-sm" />
        {preview && (
          <div className="mt-3 rounded-xl border border-sky-200 bg-sky-50 p-3 text-xs font-semibold text-sky-900" data-testid="rain-preview">
            {preview.would_move === 0
              ? t("rain_none")
              : t("rain_preview_moves", { n: preview.would_move, date: preview.shifted_to })}
            {preview.locked_stay > 0 && (
              <div className="mt-1 text-[11px] font-normal text-sky-800" data-testid="rain-locked">{t("rain_locked", { n: preview.locked_stay })}</div>
            )}
          </div>
        )}
        <div className="mt-4 flex gap-2">
          {preview && preview.would_move > 0 ? (
            <button onClick={apply} disabled={busy} data-testid="rain-apply"
              className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-full bg-brand text-sm font-bold text-white active:scale-95 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{t("rain_apply")}
            </button>
          ) : (
            <button onClick={doPreview} disabled={busy || !date} data-testid="rain-preview-btn"
              className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-full bg-slate-900 text-sm font-bold text-white active:scale-95 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}{t("rain_preview")}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
